import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/core/AuthContext";
import { useTenantStore } from "@/core/hooks/useTenant";
import { useFactoryStore } from "@/core/hooks/useFactory";

const sentryUserContextEnabled = import.meta.env.VITE_SENTRY_USER_CONTEXT_ENABLED !== "false";
const includeUserPii = import.meta.env.VITE_SENTRY_INCLUDE_USER_PII === "true";

export const SentryUserContextSync = () => {
  const { user, profile, loading } = useAuth();
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);
  const selectedFactoryId = useFactoryStore((state) => state.selectedFactoryId);

  useEffect(() => {
    if (!sentryUserContextEnabled || !Sentry.isEnabled() || loading) {
      return;
    }

    if (!user) {
      Sentry.setUser(null);
      Sentry.setTag("auth.state", "anonymous");
      Sentry.setTag("tenant.id", "none");
      Sentry.setTag("factory.id", "none");
      Sentry.setContext("auth", { authenticated: false });
      return;
    }

    const fallbackName = typeof user.user_metadata?.full_name === "string"
      ? user.user_metadata.full_name
      : undefined;
    const userPayload: {
      id: string;
      email?: string;
      username?: string;
      tenant_id?: string;
      factory_id?: string;
    } = {
      id: user.id,
      tenant_id: selectedTenantId ?? undefined,
      factory_id: selectedFactoryId ?? undefined,
    };

    if (includeUserPii) {
      userPayload.email = profile?.email ?? user.email ?? undefined;
      userPayload.username = profile?.full_name ?? fallbackName;
    }

    Sentry.setUser(userPayload);
    Sentry.setTag("auth.state", "authenticated");
    Sentry.setTag("tenant.id", selectedTenantId ?? "none");
    Sentry.setTag("factory.id", selectedFactoryId ?? "none");
    Sentry.setContext("auth", {
      authenticated: true,
      user_id: user.id,
      tenant_id: selectedTenantId,
      factory_id: selectedFactoryId,
      is_super_admin: profile?.is_super_admin ?? false,
      ...(includeUserPii
        ? {
            email: profile?.email ?? user.email,
            full_name: profile?.full_name ?? fallbackName,
          }
        : {}),
    });
  }, [loading, profile, selectedFactoryId, selectedTenantId, user]);

  return null;
};
